/* eslint-disable no-console */
import React, { useState, useEffect } from 'react';
import { useGlobal, globalizationPath } from '@rcg/ui-react';
import { navigate } from '@gatsbyjs/reach-router/';
import { useQuery, useLazyQuery } from '@apollo/client';
import Payment from '../Payment';
import UnifiedPayment from '../UnifiedPayment';
import { getSessionStorageItem, setSessionStorageItem, getCookies } from '../../commons/utils';
import {
  BOOKING_TYPES,
  BAZIL_MARKET,
  SESSION_STORAGE_CURRENT_BOOKING,
  BOOKING_TOKEN,
  PROFILE_BOOKING_DATA,
  MAIN_PROFILE_BOOKING_ID,
  COOKIE_KEYS,
  GA_PROFILE_BOOKINGS_LENGTH,
  BOOKING_ID_URL,
  NAVIGATION_PATHS,
  ROOT_PATH,
  SESSION_STORAGE_EXTERNAL_BOOKING,
  APPLICATION_NAME,
} from '../../commons/constants';
import Loader from '../../components/Loader';
import { JiraIssueCollector } from '../../components';
import {
  RETRIEVE_GUEST_PROFILE_BOOKING,
  CURRENT_BOOKING,
  CURRENT_BOOKING_GRP,
  LAUNCH_DARKLY_FLAGS,
} from '../../graphql/queries';
import setCurrentBookingVar from '../../graphql/operations/localState/setCurrentBooking';
import setCurrentPriceVar from '../../graphql/operations/localState/setPriceCurrentBooking';
import setCurrentSailingVar from '../../graphql/operations/localState/setCurrentSailing';
import { LDUser } from '../../commons/ld-user';
import Dictionary from '../../commons/dictionary';
import useDictionary from '../../hooks/useDictionary';

const PaymentContainer = () => {
  const {
    data: { lang, market, localizedDayjs },
  } = useGlobal();
  const getDictionaryValue = useDictionary(Dictionary);
  const urlPath = globalizationPath(market, lang, ROOT_PATH);
  const [profileAccessToken, setProfileAccessToken] = useState();
  const [currentBookingInfo, setCurrentBookingInfo] = useState(
    getSessionStorageItem(SESSION_STORAGE_CURRENT_BOOKING),
  );
  const [callLd, { loading: ldLoadingD, data: ldFlagsData }] = useLazyQuery(LAUNCH_DARKLY_FLAGS, {
    fetchPolicy: 'no-cache',
  });
  const ldFlags = ldFlagsData?.ldFlags;
  const showUnifiedPaymentLaunchDarkly = ldFlags?.['enable-unified-payment'];
  const gaProfileBookingsLength =
    getSessionStorageItem(GA_PROFILE_BOOKINGS_LENGTH) === null
      ? 1
      : getSessionStorageItem(GA_PROFILE_BOOKINGS_LENGTH);
  const [loginProfile, { loading: profileLoading, data: profileData }] = useLazyQuery(
    RETRIEVE_GUEST_PROFILE_BOOKING,
    {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
  );
  useEffect(() => {
    const keepAliveCookie = getCookies(COOKIE_KEYS.KEEP_ALIVE);
    const isExternalBooking = getSessionStorageItem(SESSION_STORAGE_EXTERNAL_BOOKING);
    if (profileAccessToken && !keepAliveCookie && gaProfileBookingsLength) {
      const profileBookingId =
        getSessionStorageItem(BOOKING_ID_URL) || getSessionStorageItem(MAIN_PROFILE_BOOKING_ID);
      const existingToken = getSessionStorageItem(BOOKING_TOKEN, false);
      const variables = {
        accessToken: profileAccessToken,
        bookingId: profileBookingId,
        ...(existingToken && { token: existingToken }),
      };
      if (profileAccessToken !== 1 && !isExternalBooking) loginProfile({ variables });
    }
  }, [profileAccessToken]);
  const setBookingPaymentInfo = (booking) => {
    const { price, sailing } = booking;
    setCurrentPriceVar({
      bookingId: booking.id,
      ...price,
    });
    const itineraryDays = sailing?.itinerary.days;
    const arrivalDate = localizedDayjs(
      `${sailing.sailDate}T${itineraryDays[itineraryDays.length - 1].ports[0].arrivalTime}`,
    ).add(sailing.itinerary.totalNights, 'days');
    setCurrentSailingVar({
      name: sailing.itinerary.name,
      description: sailing.itinerary.description,
      sailDate: sailing.sailDate,
      arrivalDateTime: arrivalDate,
      departureDateTime: `${sailing.sailDate}T${itineraryDays[0].ports[0].departureTime}`,
      departurePort: sailing.itinerary.departurePort,
      ship: sailing.itinerary.ship,
      totalNights: sailing.itinerary.totalNights,
      days: [],
    });
  };
  useEffect(() => {
    if (profileData?.retrieveBookingByGAProfile?.token) {
      const { token, shortProfileBooking, booking } = profileData.retrieveBookingByGAProfile;
      setSessionStorageItem(BOOKING_TOKEN, token);
      setSessionStorageItem(PROFILE_BOOKING_DATA, shortProfileBooking);
      setSessionStorageItem(MAIN_PROFILE_BOOKING_ID, booking.id);
      setSessionStorageItem(SESSION_STORAGE_CURRENT_BOOKING, booking);
      setBookingPaymentInfo(booking);
      setCurrentBookingInfo(booking);
    }
  }, [profileData]);
  const existsBookingId = getSessionStorageItem(BOOKING_ID_URL);
  const isGrpBooking = currentBookingInfo?.type === BOOKING_TYPES.GRP;
  const isTABooking = currentBookingInfo?.type === BOOKING_TYPES.TA;
  const currentBookingQuery = isGrpBooking || isTABooking ? CURRENT_BOOKING_GRP : CURRENT_BOOKING;
  const { data, loading } = useQuery(currentBookingQuery, {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    skip: !!currentBookingInfo || existsBookingId,
  });
  useEffect(() => {
    if (data) {
      const currentBooking = { ...data.currentBooking };
      setCurrentBookingInfo(currentBooking);
      setCurrentBookingVar(currentBooking);
      if (currentBooking) {
        setBookingPaymentInfo(currentBooking);
      }
    }
  }, [data]);
  useEffect(() => {
    const profile = getCookies(COOKIE_KEYS.ACCESS_TOKEN);
    if (profile) {
      setProfileAccessToken(profile);
    }
  }, []);
  useEffect(() => {
    if (currentBookingInfo) {
      setBookingPaymentInfo(currentBookingInfo);
      if (
        currentBookingInfo.type === BOOKING_TYPES.TA ||
        currentBookingInfo.type === BOOKING_TYPES.GRP ||
        currentBookingInfo.country?.code === BAZIL_MARKET
      ) {
        navigate(`${urlPath}/${NAVIGATION_PATHS[0].dashboard}`);
        return;
      }
      if (!ldFlags) {
        const ldFlagsCountry = currentBookingInfo?.country;
        const ldFlagsCurrency = currentBookingInfo?.price?.currencyCode;
        const variables = {
          ldUser: LDUser(),
          officeID: ldFlagsCountry?.office?.code || 'MIA',
          country: ldFlagsCountry?.code || 'USA',
          application: APPLICATION_NAME,
          clientApp: APPLICATION_NAME,
          language: lang || 'en',
          brand: 'ROYAL',
          currency: ldFlagsCurrency || 'USD',
        };
        callLd({ variables });
      }
    }
  }, [currentBookingInfo]);
  const isAvaiblableJiraWidget = ldFlags ? ldFlags['enable-jira-widget'] : null;
  if (loading || profileLoading || !currentBookingInfo) return <Loader display />;
  return (
    <>
      <Loader
        display={ldLoadingD}
        message={getDictionaryValue(Dictionary.loaderPaymentRedirect.key)}
      />
      {isAvaiblableJiraWidget && <JiraIssueCollector />}
      {!ldLoadingD && ldFlags ? (
        <>
          {showUnifiedPaymentLaunchDarkly ? (
            <UnifiedPayment ldFlags={ldFlags} />
          ) : (
            <Payment ldFlags={ldFlags} />
          )}
        </>
      ) : (
        ''
      )}
    </>
  );
};
export default PaymentContainer;
